/* eslint-disable*/
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Grid,
  Button,
  Typography,
  Box,
  Paper,
  Divider,
  Link,
  TextField,
  InputAdornment,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import useCommunityList from '../../../hooks/useCommunityList';
import AddCircleOutlineSharpIcon from '@material-ui/icons/AddCircleOutlineSharp';
import FilterComponent from './FilterComponent';
import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';
// import Carousel from "react-simply-carousel";
import { FROM } from '../../../constants/globalConstants';
import AmenityGrid from './AmenityGrid';
import { InfoOutlined, Search } from '@material-ui/icons';
import Pagination from '@material-ui/lab/Pagination';
import ajax from '../../../utils/ajaxHelper';

// --- URL Query Param Helpers for Amenity Filter Persistence ---
const AMENITY_ALL_FILTER_STORAGE_KEY = 'amenityAllFilters_';

const serializeAmenityFilters = (filters) => {
  if (!filters || filters.length === 0) return '';
  try {
    const json = JSON.stringify(filters);
    return btoa(unescape(encodeURIComponent(json)));
  } catch (e) {
    console.error('Failed to encode amenity filters', e);
    return '';
  }
};

const deserializeAmenityFilters = (searchStr) => {
  const params = new URLSearchParams(searchStr);
  const raw = params.get('af');
  if (!raw) return [];
  try {
    const json = decodeURIComponent(escape(atob(decodeURIComponent(raw))));
    return JSON.parse(json);
  } catch (e) {
    console.error('Failed to decode amenity filters from URL', e);
    return [];
  }
};

const saveFiltersToStorage = (communityId, filters) => {
  try {
    if (filters && filters.length > 0) {
      sessionStorage.setItem(AMENITY_ALL_FILTER_STORAGE_KEY + communityId, JSON.stringify(filters));
    } else {
      sessionStorage.removeItem(AMENITY_ALL_FILTER_STORAGE_KEY + communityId);
    }
  } catch (e) {
    // sessionStorage not available
  }
};

const loadFiltersFromStorage = (communityId) => {
  try {
    const raw = sessionStorage.getItem(AMENITY_ALL_FILTER_STORAGE_KEY + communityId);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
};
// --- End URL Query Param Helpers ---

const AmenitiesAll = ({ communityInfo, isAccessible, publicView, amenitiesHook }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  let bytes = null;
  let loggedInUserData = null;
  const userCookie = Cookies.get('user');
  const key = 'iFrasCwdJh';

  if (userCookie) {
    bytes = CryptoJS.AES.decrypt(userCookie, key);
    loggedInUserData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  }
  const [userId] = useState(loggedInUserData?.id);

  const params = useParams();
  const communityId = params?.id || communityInfo?.id;

  // Initialize selectedFilters: check URL first, then sessionStorage fallback
  const initialFiltersFromUrl = deserializeAmenityFilters(location.search);
  const initialFilters =
    initialFiltersFromUrl.length > 0 ? initialFiltersFromUrl : loadFiltersFromStorage(communityId);

  const [communityDetails, setCommunityDetails] = useState(communityInfo);
  const [amenityFilterCategories, setAmenityFilterCategories] = useState([]);
  const [activeAmenityTypes, setActiveAmenityTypes] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState(initialFilters);
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const isInitialMount = useRef(true);

  // Helper: update URL and sessionStorage with current amenity filter state
  const updateUrlParams = useCallback(
    (filters) => {
      // Save to sessionStorage (survives navigation to edit page and back)
      saveFiltersToStorage(communityId, filters);

      const params = new URLSearchParams(location.search);
      const encoded = serializeAmenityFilters(filters);
      if (encoded) {
        params.set('af', encoded);
      } else {
        params.delete('af');
      }
      const searchString = params.toString();
      navigate(`${location.pathname}${searchString ? `?${searchString}` : ''}`, { replace: true });
    },
    [navigate, location.pathname, location.search, communityId],
  );

  // Use the amenities hook if provided, otherwise fallback to old behavior
  const {
    amenities: hookAmenities,
    amenityFilters,
    loading: hookLoading,
    pagination,
    filters: hookFilters,
    updateFilters,
    goToPage,
  } = amenitiesHook || {};

  // Use hook data if available, otherwise fallback to communityDetails
  const amenities = hookAmenities || communityDetails?.amenities || [];
  const loader = hookLoading || false;
  const searchedText = hookFilters?.search || '';
  const filteredAmenities = amenities;
  // Normalize API filter shape (amenityTypes) to existing UI shape (amenityType)
  const mappedAmenityFiltersFromApi = (amenityFilters || []).map((category) => ({
    ...category,
    amenityType: category?.amenityTypes || [],
  }));
  const amenityFiltersForUI = amenitiesHook ? mappedAmenityFiltersFromApi : amenityFilterCategories;

  //console.log(amenityFilterCategories, 'AMCe');

  const [items, setItems] = useState([]);

  const { getCommunityDetailById, getAmenityCategories } = useCommunityList();
  const [potentialResultsCount, setPotentialResultsCount] = useState(null);
  const [isPotentialResultsCountLoading, setIsPotentialResultsCountLoading] = useState(false);

  // Map amenity type names (selectedFilters) to IDs expected by backend
  const mapAmenityTypeNamesToIds = (typeNames = []) => {
    if (!typeNames || typeNames.length === 0) return [];
    const nameToId = new Map();
    amenityFiltersForUI?.forEach((cat) => {
      cat?.amenityType?.forEach((t) => {
        if (t?.amenityTypeName && t?.id) nameToId.set(t.amenityTypeName, t.id);
      });
    });
    return typeNames.map((n) => nameToId.get(n)).filter(Boolean);
  };

  // REMOVED: fetchPotentialCount function
  // We now use the totalCount from the main pagination response instead of
  // making a separate API call with limit: 1. This eliminates redundant API calls.

  const [filteredAmenitiesCount, setFilteredAmenitiesCount] = useState(0);

  // Function to calculate potential result count without applying filters
  const calculatePotentialResultCount = () => {
    if (amenitiesHook) {
      return potentialResultsCount ?? pagination?.totalCount ?? 0;
    }
    return filteredAmenitiesCount || 0;
  };

  // const [myAmenities, setAmenities] = useState([]);

  useEffect(() => {
    let itemsOutArr = [];
    let amenityCatArr = [];
    amenities?.map((amenity) => {
      let itemsInnerArr = [];
      amenity?.amenityGallery?.map((galleryImage) => {
        itemsInnerArr.push({ Image: galleryImage.image });
      });
      itemsOutArr.push(itemsInnerArr);
      if (!amenityCatArr.some((obj) => obj?.id === amenity?.amenityCategory?.id)) {
        amenityCatArr.push(amenity?.amenityCategory);
      }
    });
    setItems(itemsOutArr);
  }, [amenities]);

  useEffect(() => {
    let activeAmenityTypesArr = [];

    amenities?.map((amenity) => {
      activeAmenityTypesArr.push(amenity?.amenityTypeId);
    });
    setActiveAmenityTypes(activeAmenityTypesArr);
  }, [amenities]);

  const fillCommunityDetails = async () => {
    // This function is now only needed for backward compatibility
    // when amenitiesHook is not provided
    if (!amenitiesHook) {
      let communityDetailsResults = [];
      if (params?.id) {
        communityDetailsResults = await getCommunityDetailById({
          communityId: params?.id,
          userId,
        });
        setCommunityDetails(communityDetailsResults.data);
      }
      return communityDetailsResults?.data;
    }
  };

  /* async function fetchAmenities() {
    try {
      // Make your API call here to fetch the initial data
      const response = await getAmenityByType({ communityId: params?.id }); // Replace with your actual API endpoint
      if (response.status === true) {
        // Update the 'amenities' state with the fetched data
        setAmenities(response.amenitiesResult);
      } else {
        console.error('Failed to fetch amenities data');
      }
    } catch (error) {
      console.error('Error fetching amenities data:', error);
    }
  } */

  const fillCommunityCategories = async () => {
    const amenityCategoriesArr = await getAmenityCategories();
    setAmenityFilterCategories(amenityCategoriesArr.usersResult);
  };

  // Callback to refresh amenities after toggling featured status
  const handleAmenityUpdate = async () => {
    if (amenitiesHook) {
      // If using the hook, trigger a refresh by re-applying current filters
      const typeIds = mapAmenityTypeNamesToIds(selectedFilters);
      updateFilters({
        search: searchedText,
        amenityTypes: typeIds,
      });
    } else {
      // Fallback to old behavior - refresh community details
      await fillCommunityDetails();
    }
  };

  useEffect(() => {
    if (!amenitiesHook) return;
    console.log('Amenity filters:', amenityFilters || []);
  }, [amenitiesHook, amenityFilters]);

  /* useEffect(() => {
    fillCommunityDetails();
    fillCommunityCategories();
    // fetchAmenities();
  }, []); */

  // When pending filters/search change while the popover is open, fetch exact preview count.
  // This uses get-amenities-paginated totalCount so AND logic is applied server-side.
  useEffect(() => {
    if (!amenitiesHook || !open || !params?.id) {
      setPotentialResultsCount(null);
      setIsPotentialResultsCountLoading(false);
      return;
    }

    let isMounted = true;
    setIsPotentialResultsCountLoading(true);

    const timeoutId = setTimeout(async () => {
      try {
        const amenityTypeIds = mapAmenityTypeNamesToIds(selectedFilters);
        const response = await ajax.post(
          'community/get-amenities-paginated',
          {
            communityId: params.id,
            page: 1,
            limit: 1,
            search: searchedText,
            amenityTypes: amenityTypeIds,
            amenityCategories: hookFilters?.amenityCategories || [],
            featuredOnly: hookFilters?.featuredOnly || false,
          },
          {
            networkParams: { showLoader: false },
          },
        );
        if (isMounted) {
          setPotentialResultsCount(response?.pagination?.totalCount || 0);
        }
      } catch (error) {
        if (isMounted) {
          setPotentialResultsCount(0);
        }
      } finally {
        if (isMounted) {
          setIsPotentialResultsCountLoading(false);
        }
      }
    }, 250);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [
    amenitiesHook,
    open,
    params?.id,
    selectedFilters,
    searchedText,
    hookFilters?.amenityCategories,
    hookFilters?.featuredOnly,
  ]);

  const showPreviewCountLoader =
    amenitiesHook && selectedFilters.length > 0 && isPotentialResultsCountLoading;
  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterSelection = (filter) => {
    // Check if the filter is already selected
    const isFilterSelected = selectedFilters.includes(filter);

    // If the filter is selected, remove it; otherwise, add it
    const updatedFilters = isFilterSelected
      ? selectedFilters.filter((selected) => selected !== filter)
      : [...selectedFilters, filter];

    // Update the selected filters
    setSelectedFilters(updatedFilters);

    document.activeElement.blur();
  };

  const handleSearchChange = (event) => {
    const searchText = event?.target?.value;

    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    const newTimeout = setTimeout(() => {
      if (amenitiesHook && updateFilters) {
        // Use the hook's update function
        updateFilters({ search: searchText });
      } else {
        // Fallback to old behavior
        applyFiltersAndSearch(selectedFilters, searchText);
      }
    }, 300); // 300ms delay for better performance

    setDebounceTimeout(newTimeout);
  };

  //console.log(selectedFilters, 'FF4');

  const resetFilters = () => {
    setSelectedFilters([]);
    // Clear filters from URL and sessionStorage
    updateUrlParams([]);
    // Keep the search text - only reset filters
    applyFiltersAndSearch([], searchedText);
  };

  //console.log(filteredAmenities, 'FMA');

  const applyFiltersAndSearch = (filters = selectedFilters, searchText = searchedText) => {
    // If using the hook, filters are applied server-side
    if (amenitiesHook && updateFilters) {
      const amenityTypeIds = mapAmenityTypeNamesToIds(filters);
      updateFilters({
        search: searchText,
        amenityTypes: amenityTypeIds, // backend expects IDs
      });
      return;
    }

    // Fallback to client-side filtering for backward compatibility
    let filteredAmenitiesLocal = amenities || [];

    // Apply filter-based filtering
    if (filters.length > 0) {
      filteredAmenitiesLocal = filteredAmenitiesLocal.filter((amenity) =>
        filters.includes(amenity.amenityType.amenityTypeName),
      );
    }

    // Apply search-based filtering
    if (searchText && searchText.trim() !== '') {
      const searchLower = searchText.toLowerCase().trim();
      filteredAmenitiesLocal = filteredAmenitiesLocal.filter((amenity) => {
        return (
          amenity?.amenityName?.toLowerCase().includes(searchLower) ||
          amenity?.amenityType?.amenityTypeName?.toLowerCase().includes(searchLower) ||
          amenity?.amenityCategory?.amenityCategoryName?.toLowerCase().includes(searchLower) ||
          amenity?.amenityDescription?.toLowerCase().includes(searchLower)
        );
      });
    }

    // For backward compatibility, we would need to update local state here
    // but since we're using the hook, this is mainly for fallback
    setFilteredAmenitiesCount(filteredAmenitiesLocal.length);
  };

  const applyFiltersAndFetch = () => {
    applyFiltersAndSearch();
    // Sync filters to URL and sessionStorage
    updateUrlParams(selectedFilters);
    // Close the filter popover
    setAnchorEl(null);
  };

  useEffect(() => {
    // Only apply search when not using the hook (backward compatibility)
    if (!amenitiesHook && communityDetails?.amenities) {
      // Only apply search, not filters - filters should only apply on "See Results" click
      applyFiltersAndSearch([], searchedText);
    }
  }, [searchedText, communityDetails?.amenities, amenitiesHook]);

  useEffect(() => {
    fillCommunityDetails();
    fillCommunityCategories();
    // fetchAmenities();
  }, []);

  // Auto-apply filters from URL/sessionStorage on initial mount
  // Wait for amenityFiltersForUI to be populated so mapAmenityTypeNamesToIds can resolve names → IDs
  useEffect(() => {
    if (
      isInitialMount.current &&
      initialFilters.length > 0 &&
      amenityFiltersForUI &&
      amenityFiltersForUI.length > 0
    ) {
      isInitialMount.current = false;
      applyFiltersAndSearch(initialFilters, searchedText);
      // Sync to URL if filters came from sessionStorage
      if (initialFiltersFromUrl.length === 0) {
        updateUrlParams(initialFilters);
      }
    } else if (!initialFilters.length) {
      isInitialMount.current = false;
    }
  }, [amenitiesHook, amenities, amenityFiltersForUI]); // re-run when hook data or filter categories load

  useEffect(() => {
    // Update count based on hook data or community details
    if (amenitiesHook && pagination) {
      setFilteredAmenitiesCount(pagination.totalCount);
    } else if (!amenitiesHook && communityDetails?.amenities) {
      setFilteredAmenitiesCount(communityDetails?.amenities.length);
    }
  }, [amenitiesHook, pagination, communityDetails]);

  return (
    <Paper className="WhitePaper" component="div">
      <Grid item xs={12}>
        <Box className="FlexSide ViewAllAmenitiesHeader">
          <Box>
            <Link
              className="BackButtonWrapper"
              underline="none"
              href={`/#/app/community-details/${communityDetails?.id}#Section1`}
            >
              <Typography className="BackButton BackButtonMb" variant="body1">
                <ArrowBackIcon color="primary" className="BackButtonArrow" />
                Feature an Amenity
              </Typography>
            </Link>
            <Typography variant="body2" className="AddFeatureAmenityLabel">
              Add an Amenity to your Community’s page by select from the list of amenities we’ve
              sourced for below or feature your own!
            </Typography>
          </Box>
          <Box className="ViewAllAmenitiesInner">
            <Button
              className="AddFeatureAmenityButton"
              variant="contained"
              color="primary"
              startIcon={<AddCircleOutlineSharpIcon />}
              href={`/#/app/community-details-edit-add-new-amenity/${FROM.COMMUNITY_DETAILS}/${communityDetails?.id}/default`}
            >
              Feature New Amenity
            </Button>
          </Box>
        </Box>
      </Grid>

      <Box className="ViewAllAmenitiesInner">
        <Grid item xs={12}>
          <Box className="AlertBoxContainer">
            <InfoOutlined />
            <Box>
              <Typography variant="body1" className="AlertBoxHeading">
                Heads up!
              </Typography>
              <Typography variant="body1" className="AlertBoxSubHeading LightgreyColor">
                Amenity data is automatically categorized based on available data, but some
                classifications may not be a perfect fit. Before featuring an amenity, double-check
                the amenity type aligns with your expectation. If something seems off, you can
                update or adjust as needed.
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <TextField
            className="FeatureAmenitySearchBox"
            value={searchedText}
            onChange={handleSearchChange}
            variant="outlined"
            fullWidth
            type="text"
            placeholder="Search available amenities"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search className="GreenColor" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid container>
          <Grid item xs={12}>
            <Typography color="primary" variant="h2" className="BoxHeading">
              Amenities
            </Typography>
          </Grid>
          {isAccessible && !publicView && (
            <>
              <Grid item xs={12} className="AddAmenityRow">
                <Box className="AddAmenityContentBox">
                  <Typography variant="body2" className="AddLabel">
                    Inputting amenities helps users find your community when filtering.
                  </Typography>
                  <Button
                    className="AddButtonBox"
                    variant="outlined"
                    color="primary"
                    fullWidth
                    href={`/#/app/community-details-edit-add-new-amenity/${FROM.AMENITIES_LiST}/${communityDetails?.id}/default`}
                    startIcon={<AddCircleOutlineSharpIcon />}
                  >
                    Add An Amenity
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} className="AddAmenityDividerRow">
                <Divider className="DividerCD"></Divider>
              </Grid>
            </>
          )}
          <Grid item xs={12} className="CommonDetailBoxGrid">
            <Box className="FilterTagSecond FilterTagContent">
              {amenityFilterCategories?.map((category) => {
                return <Button className="FilterTag">{category?.amenityCategoryName}</Button>;
              })}
            </Box>
          </Grid>
          <Grid item xs={12} className="">
            <FilterComponent
              resultCount={filteredAmenitiesCount}
              selectedFilters={selectedFilters}
              anchorEl={anchorEl}
              open={open}
              filtersGrouped={(amenityFiltersForUI || []).map((cat) => ({
                id: cat?.id || cat?.amenityCategoryId,
                categoryName: cat?.amenityCategoryName,
                types: (cat?.amenityType || []).map((t) => ({
                  id: t.id,
                  typeName: t.amenityTypeName,
                })),
              }))}
              onFilterClick={handleFilterClick}
              onFilterClose={handleFilterClose}
              onFilterSelection={handleFilterSelection}
              onResetFilters={resetFilters}
              onFilterResultClick={applyFiltersAndFetch}
              calculatePotentialResultCount={calculatePotentialResultCount}
              isLoading={loader}
              hasNoFilters={!amenityFiltersForUI || amenityFiltersForUI.length === 0}
              showPreviewCountLoader={showPreviewCountLoader}
              title="Amenity Filters"
            />
          </Grid>
        </Grid>
        {!loader ? (
          amenities?.length > 0 ? (
            <Box className="CommonDetailBoxGridSlider">
              <AmenityGrid
                filteredAmenities={filteredAmenities}
                amenities={amenities}
                isAccessible={isAccessible}
                publicView={publicView}
                setCommunityDetails={setCommunityDetails}
                onAmenityUpdate={handleAmenityUpdate}
                editFrom={FROM.AMENITIES_LiST}
              />
            </Box>
          ) : (
            <Grid container>
              <Grid item xs={12}>
                <Box className="EmptyNoteBox AmenitiesEmptyNote">
                  <Box className="EmptySearchBox BgFifth">
                    <Typography variant="h3" className="EmptySearchHeading" component="div">
                      No amenities to show—yet!
                    </Typography>
                    <Typography variant="body1" className="EmptySearchSubHeading" component="div">
                      We’re working on adding great amenities for you to feature in your community.
                      Check back soon! In the meantime, why not feature one yourself?
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )
        ) : (
          'Loading...'
        )}

        {/* Pagination Controls - only show when using the hook */}
        {amenitiesHook && pagination && pagination.totalPages > 1 && (
          <Grid container direction="row">
            <Grid item xs={12}>
              <Box className="PaginationWrapper BasicPagination">
                <Typography variant="body2" component="span" className="PaginationNumber">
                  <Box component="span">{(pagination.currentPage - 1) * pagination.limit + 1}</Box>-
                  <Box component="span">
                    {Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)}
                  </Box>{' '}
                  of <Box component="span">{pagination.totalCount}</Box> results
                </Typography>
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.currentPage}
                  onChange={(_, page) => goToPage(page)}
                  color="primary"
                />
              </Box>
            </Grid>
          </Grid>
        )}
      </Box>
    </Paper>
  );
};

export default AmenitiesAll;
